import { KeyboardEvent, PointerEvent, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ResizeIcon } from "../../icons";

const MinHeight = 96;
const KeyStep = 24;

interface RichTextFooterProps {
  path: string;
  /** The height of what is being resized now, or nothing when it cannot be. */
  measure: () => number | undefined;
  onResize: (height: number) => void;
}

/** Under the text: the elements the caret is in at the left, and a handle at the right to make the text taller or shorter. */
export function RichTextFooter({ path, measure, onResize }: RichTextFooterProps) {
  const drag = useRef<{ y: number; height: number } | null>(null);

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    const height = measure();

    if (height !== undefined) {
      drag.current = { y: event.clientY, height };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }
  };

  const onPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    if (drag.current) {
      onResize(Math.max(MinHeight, drag.current.height + event.clientY - drag.current.y));
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const height = measure();
    const direction = event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0;

    if (direction && height !== undefined) {
      event.preventDefault();
      onResize(Math.max(MinHeight, height + direction * KeyStep));
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-between gap-2 border-top bg-white rounded-bottom-2 px-2 py-1 fs-7 text-body-secondary">
      <span aria-label="Element path" className="text-truncate">
        {path}
      </span>
      <button
        type="button"
        className="btn btn-sm border-0 shadow-none p-0 text-body-secondary lh-1"
        style={{ cursor: "nwse-resize", touchAction: "none" }}
        aria-label="Resize"
        title="Drag to resize"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={() => {
          drag.current = null;
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
        onKeyDown={onKeyDown}
      >
        <FontAwesomeIcon icon={ResizeIcon} />
      </button>
    </div>
  );
}
